import { Box, Button, Stack } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { endOfDay } from "date-fns";
import {
  complement,
  concat,
  either,
  equals,
  filter,
  find,
  map,
  path,
  pipe,
  prop,
  propEq,
  uniqBy,
} from "ramda";
import { useMemo, useState } from "react";
import { ButtonMenu } from "../../components/ButtonMenu";
import { Icon } from "../../components/Icon";
import { generalMessages } from "../../components/messages";
import { useMsg } from "../../components/Msg/Msg";
import { CancelSessionButton } from "../../components/ScheduledSessions/CancelSessionButton";
import {
  DateCell,
  TLCell,
  TLChipsCell,
  TLTableWithHeader,
  UserCell,
} from "../../components/Table";
import { H2, P } from "../../components/Typography";
import { allPropsPass } from "../../utils/fp.utils";
import { formatName } from "../Coaches/coach.utils";
import { ConfirmModal } from "../Modal/ConfirmModal";
import { SlotChip } from "../Team/Team.page";
import {
  useAllCoachSessionsQuery,
  useUpdateCompletedSessionStatusMutation,
} from "./api";
import { clientsMessages } from "./messages";

const SESSION_STATUS = {
  COMPLETED: "COMPLETED",
  UPCOMING: "UPCOMING",
  CANCELED: "CANCELED",
  PENDING: "PENDING",
};

// TODO: abstract
const useStatusChangeWithConfirm = ({ mutation }) => {
  const generalMsg = useMsg({ dict: generalMessages });
  const [data, setData] = useState();

  ConfirmModal.useModal(
    useMemo(() => {
      return {
        open: !!data?.payload,
        iconName: data?.iconName,
        title: data?.title,
        desc: data?.desc,
        error: mutation.error,
        onClose: () => setData(),
        getButtons: ({ onClose }) => [
          {
            variant: "outlined",
            type: "button",
            children: generalMsg("general.no"),
            onClick: () => onClose(),
          },
          {
            component: Button,
            variant: "contained",
            // color: "error",
            type: "button",
            children: generalMsg("general.yes"),
            disabled: mutation.isPending,
            loading: mutation.isPending,
            onClick: () =>
              mutation.mutate(data?.payload, {
                onSuccess: () => setData(),
              }),
          },
        ],
      };
    }, [
      data?.desc,
      data?.iconName,
      data?.payload,
      data?.title,
      generalMsg,
      mutation,
    ]),
  );

  return {
    withConfirmation: ({ payload, iconName, title, desc }) =>
      setData({ payload, iconName, title, desc }),
  };
};

const filterPredicate = (sessionsFilter) => (session) =>
  allPropsPass({
    client: either(complement(Boolean), equals(session.client)),
    status: either(complement(Boolean), equals(session.status)),
    from: (from) => !from || new Date(session.date) >= new Date(from),
    to: (to) => !to || new Date(session.date) <= new Date(to),
  })(sessionsFilter);

export const ClientsSessions = () => {
  const msg = useMsg({ dict: clientsMessages });
  const [sessionsFilter, setSessionsFilter] = useState({
    client: undefined,
    status: undefined,
    from: undefined,
    to: undefined,
  });
  const query = useAllCoachSessionsQuery();
  const updateStatusMutation = useUpdateCompletedSessionStatusMutation();
  const sessionStatuses = useMemo(
    () => ({
      [SESSION_STATUS.COMPLETED]: {
        label: msg("clients.sessions.filter.status.completed"),
        bgcolor: "#66C61C4D",
      },
      [SESSION_STATUS.UPCOMING]: {
        label: msg("clients.sessions.filter.status.upcoming"),
        bgcolor: "#EAECF0",
      },
      [SESSION_STATUS.CANCELED]: {
        label: msg("clients.sessions.filter.status.canceled"),
        bgcolor: "#F044384D",
      },
      [SESSION_STATUS.PENDING]: {
        label: msg("clients.sessions.filter.status.pending"),
        bgcolor: "#EAAA084D",
      },
    }),
    [msg],
  );
  const { withConfirmation } = useStatusChangeWithConfirm({
    mutation: updateStatusMutation,
  });
  // console.log("[ClientsSessions.rndr]", { query, sessionsFilter });

  const columns = [
    {
      label: msg("clients.sessions.table.col.time"),
      key: "date",
      render: (row) => <DateCell utcStr={row.date} />,
    },
    {
      label: msg("clients.sessions.table.col.name"),
      key: "name",
      render: (row) => <UserCell email={row.client} name={formatName(row)} />,
    },
    {
      label: msg("clients.sessions.table.col.status"),
      key: "status",
      render: (row) => {
        return <TLChipsCell chips={[sessionStatuses[row.status]]} />;
      },
    },
    {
      label: msg("clients.sessions.table.col.action"),
      key: "action",
      render: (row) => (
        <TLCell>
          {row.status === SESSION_STATUS.UPCOMING && (
            <CancelSessionButton
              sessionId={row.id}
              name={formatName(row)}
              time={row.date}
              username={row.client}
              type={"coach"}
            />
          )}
          {row.status === SESSION_STATUS.PENDING && (
            <ButtonMenu
              button={{
                variant: "outlined",
                endIcon: <Icon name="KeyboardArrowDown" />,
                children: msg("clients.sessions.action.resolve"),
                "aria-label": msg("clients.sessions.action.resolve"),
                disabled: updateStatusMutation.isPending,
              }}
              items={[
                {
                  label: msg("clients.sessions.action.resolve.completed"),
                  onClick: () => {
                    withConfirmation({
                      iconName: "CheckCircle",
                      title: msg("clients.sessions.action.resolve.completed"),
                      desc: msg(
                        "clients.sessions.action.resolve.completed.confirm.desc",
                      ),
                      payload: {
                        id: row.id,
                        status: SESSION_STATUS.COMPLETED,
                      },
                    });
                  },
                },
                {
                  label: msg("clients.sessions.action.resolve.canceled"),
                  sx: { color: "red" },
                  onClick: () => {
                    withConfirmation({
                      iconName: "Cancel",
                      title: msg("clients.sessions.action.resolve.canceled"),
                      desc: msg(
                        "clients.sessions.action.resolve.canceled.confirm.desc",
                      ),
                      payload: {
                        id: row.id,
                        status: SESSION_STATUS.CANCELED,
                      },
                    });
                  },
                },
              ]}
            />
          )}
        </TLCell>
      ),
    },
  ];
  const rows = query.data?.content || [];

  return (
    <>
      <TLTableWithHeader
        title={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              minHeight: 30,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <H2>{msg("clients.sessions.title")}</H2>
              <SlotChip
                loading={query.isFetching}
                sx={{ display: "inline-flex", p: 0.75, ml: 2 }}
              >
                {msg("clients.sessions.title.count.badge", {
                  count: query.data?.content?.length,
                })}
              </SlotChip>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ButtonMenu
                button={{
                  variant: sessionsFilter.client ? "contained" : "outlined",
                  endIcon: <Icon name="KeyboardArrowDown" />,
                  children: sessionsFilter.client
                    ? pipe(
                        find(propEq(sessionsFilter.client, "client")),
                        (item) => formatName(item) || item.client,
                      )(rows)
                    : msg("clients.sessions.filter.client"),
                  "aria-label": msg("clients.sessions.filter.client"),
                }}
                items={pipe(
                  uniqBy(prop("client")),
                  map((item) => ({
                    selected: item.client === sessionsFilter.client,
                    label: formatName(item) || item.client,
                    onClick: () =>
                      setSessionsFilter((prev) => ({
                        ...prev,
                        client: item.client,
                        // prev.client === item.client ? undefined : item.client,
                      })),
                  })),
                  concat([
                    {
                      selected: !sessionsFilter.client,
                      label: msg("clients.sessions.filter.client.all"),
                      onClick: () =>
                        setSessionsFilter((prev) => ({
                          ...prev,
                          client: undefined,
                        })),
                    },
                  ]),
                )(rows)}
              />

              <ButtonMenu
                button={{
                  variant: sessionsFilter.status ? "contained" : "outlined",
                  endIcon: <Icon name="KeyboardArrowDown" />,
                  children: sessionsFilter.status
                    ? sessionStatuses[sessionsFilter.status]?.label
                    : msg("clients.sessions.filter.status"),
                  "aria-label": msg("clients.sessions.filter.status"),
                }}
                items={pipe(
                  uniqBy(prop("status")),
                  map((item) => ({
                    selected: item.status === sessionsFilter.status,
                    label: sessionStatuses[item.status]?.label || item.status,
                    onClick: () =>
                      setSessionsFilter((prev) => ({
                        ...prev,
                        status: item.status,
                        // prev.status === item.status ? undefined : item.status,
                      })),
                  })),
                  concat([
                    {
                      selected: !sessionsFilter.status,
                      label: msg("clients.sessions.filter.status.all"),
                      onClick: () =>
                        setSessionsFilter((prev) => ({
                          ...prev,
                          status: undefined,
                        })),
                    },
                  ]),
                )(query.data?.content || [])}
              />

              <ButtonMenu
                button={{
                  variant:
                    sessionsFilter.from || sessionsFilter.to
                      ? "contained"
                      : "outlined",
                  endIcon: <Icon name="KeyboardArrowDown" />,
                  children:
                    sessionsFilter.from || sessionsFilter.to ? (
                      <Icon name="CalendarMonth" />
                    ) : (
                      msg("clients.sessions.filter.time")
                    ),
                  "aria-label": msg("clients.sessions.filter.time"),
                }}
                renderItems={() => {
                  return (
                    <Stack direction="row" spacing={1} sx={{ p: 1 }}>
                      <DatePicker
                        label={msg("clients.sessions.filter.time.from")}
                        value={sessionsFilter.from}
                        onChange={(from) =>
                          setSessionsFilter((prev) => ({ ...prev, from }))
                        }
                        slotProps={{ field: { clearable: true } }}
                        maxDate={
                          sessionsFilter.to
                            ? new Date(+sessionsFilter.to)
                            : undefined
                        }
                      />
                      <DatePicker
                        label={msg("clients.sessions.filter.time.to")}
                        value={sessionsFilter.to}
                        onChange={(to) =>
                          setSessionsFilter((prev) => ({
                            ...prev,
                            to: to ? endOfDay(to) : to, // clearable
                          }))
                        }
                        slotProps={{ field: { clearable: true } }}
                        minDate={
                          sessionsFilter.from
                            ? new Date(+sessionsFilter.from)
                            : undefined
                        }
                      />
                    </Stack>
                  );
                }}
              />
            </Box>
          </Box>
          // </Box>
        }
        subheader={<P mt={1.5}>{msg("clients.sessions.sub")}</P>}
        columns={columns}
        query={query}
        getRowsFromQuery={pipe(
          path(["data", "content"]),
          filter(filterPredicate(sessionsFilter)),
        )}
      />
    </>
  );
};
